/**
 * Which health states the endpoint table shows.
 *
 * This filters in the browser, not on the server. The backend's `health=` query
 * parameter takes a single state, and the whole point of this control is to ask
 * for "down and degraded" — the two states worth looking at — as one view.
 * Since the table already holds all 1,062 rows for the window, filtering here
 * is a set membership test and costs nothing.
 *
 * `unknown` is a first-class option rather than something hidden by default.
 * Roughly 1,058 of the 1,062 endpoints are silent in any given window, and a
 * filter that quietly dropped them would make the registry look four endpoints
 * long. It is on by default and easy to switch off.
 */
import { HEALTH_STATES } from '../api/types'
import type { HealthState } from '../api/types'
import { useI18n } from '../i18n'
import { HEALTH_STYLES, numberFormat } from './statusColors'

export const ALL_HEALTH_STATES: ReadonlySet<HealthState> = new Set(HEALTH_STATES)

/** Order shown left-to-right: worst first, matching the table's default sort. */
const DISPLAY_ORDER: readonly HealthState[] = ['down', 'degraded', 'unknown', 'healthy']

export interface StatusFilterProps {
  /** The states currently visible. Empty means the table shows nothing. */
  selected: ReadonlySet<HealthState>
  onChange: (next: ReadonlySet<HealthState>) => void
  /** Optional per-state totals from the overview, shown on each chip. */
  counts?: Partial<Record<HealthState, number>>
}

/** Immutable toggle: returns a new set so React sees the change. */
export function toggleHealth(
  selected: ReadonlySet<HealthState>,
  state: HealthState,
): ReadonlySet<HealthState> {
  const next = new Set(selected)
  if (!next.delete(state)) next.add(state)
  return next
}

export function StatusFilter({ selected, onChange, counts }: StatusFilterProps) {
  const { t } = useI18n()

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-medium text-muted">{t('domain.health')}</span>

      {DISPLAY_ORDER.map((state) => {
        const style = HEALTH_STYLES[state]
        const active = selected.has(state)
        const count = counts?.[state]
        return (
          <button
            key={state}
            type="button"
            aria-pressed={active}
            title={t(`dashboard.healthHint.${state}`)}
            onClick={() => onChange(toggleHealth(selected, state))}
            className={[
              'flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors',
              active
                ? 'border-border bg-surface text-fg'
                : // Off is drawn as drained, not as absent: the state is still a
                  // thing that exists, it is just not on screen right now.
                  'border-transparent bg-transparent text-muted opacity-60 hover:opacity-100',
            ].join(' ')}
          >
            <span aria-hidden="true" className={`h-2 w-2 rounded-full ${style.bg}`} />
            {t(`domain.health.${state}`)}
            {count !== undefined && (
              // Rendered even at 0 — "nothing is down" is an answer, and a chip
              // that vanishes at zero is indistinguishable from a missing one.
              <span className="num text-muted">{numberFormat.format(count)}</span>
            )}
          </button>
        )
      })}

      {selected.size === 0 && (
        <p role="alert" className="flex items-center gap-2 text-xs text-degraded">
          {t('dashboard.statusFilter.noneSelected')}
          <button
            type="button"
            onClick={() => onChange(new Set(HEALTH_STATES))}
            className="rounded border border-border px-1.5 py-0.5 font-medium text-fg"
          >
            {t('dashboard.statusFilter.showAll')}
          </button>
        </p>
      )}
    </div>
  )
}
